<template>
  <div class="course-info">
    <el-form :model="form" ref="formRef" label-width="120px" :rules="rules">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="课程名称" prop="courseName">
            <el-input
              style="width:360px"
              v-model.trim="form.courseName"
              placeholder="请输入"
              maxlength="30"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="一级标签" prop="categoryIdOne">
            <el-select
              v-model="form.categoryIdOne"
              placeholder="请选择"
              clearable
              filterable
              style="width:360px"
              @change="handleCategoryOneChange"
            >
              <el-option
                v-for="item in categoryOneOptions"
                :key="item.categoryId"
                :label="item.categoryName"
                :value="item.categoryId"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="二级标签" prop="categoryIdTwo">
            <el-select
              v-model="form.categoryIdTwo"
              placeholder="请先选择一级标签"
              clearable
              filterable
              style="width:360px"
              :disabled="!form.categoryIdOne"
            >
              <el-option
                v-for="item in categoryTwoOptions"
                :key="item.categoryId"
                :label="item.categoryName"
                :value="item.categoryId"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="24">
          <el-form-item label="课程封面" prop="courseImage">
            <UploadCropImage
              v-model="form.courseImage"
              aspect-ratio="16:9"
              :output-width="800"
              :max-size="5"
              @input="
                $refs.formRef && $refs.formRef.validateField('courseImage')
              "
              @remove="
                $refs.formRef && $refs.formRef.validateField('courseImage')
              "
            />
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="课程描述" prop="courseIntroduce">
            <RichEditor
              v-model="form.courseIntroduce"
              placeholder="请输入课程描述"
              height="320px"
              @change="
                $refs.formRef && $refs.formRef.validateField('courseIntroduce')
              "
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <div class="course-btn">
      <el-button
        plain
        round
        size="small"
        type="primary"
        :loading="submitLoading"
        @click="save"
        >保存并下一步</el-button
      >
      <el-button plain round size="small" @click="cancel">取消</el-button>
    </div>
  </div>
</template>
<script>
import { getCourseDetail, updateCourse, addCourse } from "@/api/course";
import {
  getOneLevelCategories,
  getTwoLevelCategories
} from "@/api/content/courseCategory";
import UploadCropImage from "@/components/UploadCropImage.vue";
import RichEditor from "@/components/RichEditor.vue";

function defaultForm() {
  return {
    courseId: undefined,
    courseCode: "",
    courseName: "",
    categoryIdOne: undefined,
    categoryIdTwo: undefined,
    courseIntroduce: "",
    courseImage: ""
  };
}

function isEmptyHtml(html) {
  if (!html) return true;
  const str = String(html);
  // 仅上传图片/视频也算有内容
  if (/<(img|video|iframe)\b/i.test(str)) return false;
  const text = str
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, "")
    .trim();
  return !text;
}

export default {
  name: "CourseInfo",
  components: {
    UploadCropImage,
    RichEditor
  },
  props: {
    courseCode: String || Number
  },
  data() {
    return {
      form: defaultForm(),
      categoryOneOptions: [],
      categoryTwoAll: [],
      rules: {
        courseName: [{ required: true, message: "请输入", trigger: "blur" }],
        categoryIdOne: [
          { required: true, message: "请选择一级标签", trigger: "change" }
        ],
        courseImage: [
          { required: true, message: "请上传课程封面", trigger: "change" }
        ],
        courseIntroduce: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (isEmptyHtml(value)) {
                callback(new Error("请输入课程描述"));
                return;
              }
              callback();
            },
            trigger: ["blur", "change"]
          }
        ]
      },
      submitLoading: false
    };
  },
  computed: {
    categoryTwoOptions() {
      if (!this.form.categoryIdOne) return [];
      return this.categoryTwoAll.filter(
        item =>
          String(item.categoryParentId) === String(this.form.categoryIdOne)
      );
    }
  },
  methods: {
    normalizeList(result) {
      const data = result && result.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.rows)) return data.rows;
      return [];
    },
    async loadCategories() {
      try {
        const [oneRes, twoRes] = await Promise.all([
          getOneLevelCategories(),
          getTwoLevelCategories()
        ]);
        this.categoryOneOptions = this.normalizeList(oneRes);
        this.categoryTwoAll = this.normalizeList(twoRes);
      } catch (e) {}
    },
    handleCategoryOneChange() {
      this.form.categoryIdTwo = undefined;
    },
    cancel() {
      this.$router.replace("/course");
    },
    isValidCourseData(data) {
      return !!(data && (data.courseId != null || data.courseCode));
    },
    redirectIfCourseMissing(msg) {
      if (msg) {
        this.$message.warning(msg);
      }
      this.$router.replace("/course");
    },
    async getInfo() {
      if (!this.courseCode) {
        return;
      }
      try {
        const res = await getCourseDetail(this.courseCode);
        const data = (res && res.data) || null;
        if (!this.isValidCourseData(data)) {
          this.redirectIfCourseMissing("课程不存在");
          return;
        }
        this.form = {
          ...defaultForm(),
          courseId: data.courseId,
          courseCode: data.courseCode,
          courseName: data.courseName || "",
          categoryIdOne: data.categoryIdOne,
          categoryIdTwo: data.categoryIdTwo,
          courseIntroduce: data.courseIntroduce || "",
          courseImage: data.courseImage || ""
        };
      } catch (e) {
        this.redirectIfCourseMissing();
      }
    },
    async save() {
      if (this.submitLoading) return;
      try {
        await this.$refs.formRef.validate();
      } catch (e) {
        return;
      }
      if (isEmptyHtml(this.form.courseIntroduce)) {
        this.$message.warning("请输入课程描述");
        this.$refs.formRef.validateField("courseIntroduce");
        return;
      }
      this.submitLoading = true;
      try {
        const payload = {
          courseId: this.form.courseId,
          courseCode: this.form.courseCode,
          courseName: this.form.courseName,
          categoryIdOne: this.form.categoryIdOne,
          categoryIdTwo: this.form.categoryIdTwo,
          courseIntroduce: this.form.courseIntroduce,
          courseImage: this.form.courseImage
        };
        const res = this.courseCode
          ? await updateCourse(payload)
          : await addCourse(payload);
        this.$emit("success", res.data);
      } finally {
        this.submitLoading = false;
      }
    }
  },
  created() {
    this.loadCategories();
    this.getInfo();
  }
};
</script>
<style scoped lang="less">
.course-info {
  width: 80%;
  margin: 0 auto;

  .course-btn {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/CourseEdit/CourseInfo.vue